import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Award } from 'lucide-react';
import AuthorsLink from '../../Views/Helpers/AuthorsLink';
import { resolvePublicationAuthors } from '../../../helpers/resolvePublicationAuthors';
import CardImage from '../Views/CardImage';
import { activePublicationLinks } from '../../../helpers/publicationLinks';
import '../../Views/academicons/css/academicons.min.css';
import { vocabTitle } from '../../../helpers/content';

const getAuthorName = (author) =>
  author?.publication_name || author?.full_name || author?.title || '';

const FlexPublicationCard = memo(
  ({ item, showAuthorLinks = true, compact, hideLink }) => {
    const authors = useMemo(() => resolvePublicationAuthors(item), [item]);
    const publicationPublishedIn = item.publication_published_in;
    const publicationYear = item.publication_year;
    const publicationYearLabel = vocabTitle(publicationYear);
    // Some "published in" citations already end in the year (and often the
    // month too, e.g. "..., October 2025.") - showing the Year field next to
    // it would just repeat it. Other sites built on this same add-on write
    // "published in" without a year, where the separate field is the only
    // source of it, so this has to be a per-item check, not a fixed toggle.
    const yearAlreadyInPublishedIn =
      !!publicationPublishedIn &&
      !!publicationYearLabel &&
      String(publicationPublishedIn).includes(String(publicationYearLabel));

    const activeLinks = activePublicationLinks(item);

    const title = (
      <h4 className="cards__title">{item.title ? item.title : item.id}</h4>
    );

    return (
      <div className="cards__item cards__item--fullwidth">
        {!compact && (
          <CardImage
            item={item}
            fallback={'/publication_default_image.svg'}
            containerClass="cards__image-container--left"
            href={hideLink ? undefined : item['@id']}
          />
        )}
        <div className="cards__content">
          {hideLink ? (
            title
          ) : (
            <UniversalLink href={item['@id']}>{title}</UniversalLink>
          )}

          {authors.length > 0 && (
            <p className="cards__info cards__info--authors">
              {showAuthorLinks ? (
                <AuthorsLink authors={authors} inline />
              ) : (
                authors.map(getAuthorName).filter(Boolean).join(', ')
              )}
            </p>
          )}

          {(publicationPublishedIn ||
            (publicationYearLabel && !yearAlreadyInPublishedIn)) && (
            <p className="cards__info">
              {publicationPublishedIn}
              {publicationPublishedIn &&
                publicationYearLabel &&
                !yearAlreadyInPublishedIn &&
                ', '}
              {!yearAlreadyInPublishedIn && publicationYearLabel}
            </p>
          )}

          {(activeLinks.length > 0 ||
            item.is_open_access ||
            item.is_best_paper) && (
            <div className="cards__action-links">
              {activeLinks.map(({ key, label, href }) => (
                <a
                  key={key}
                  href={href}
                  className="cards__action-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label}
                </a>
              ))}
              {(item.is_open_access || item.is_best_paper) && (
                <div className="cards__status-icons">
                  {item.is_open_access && (
                    <span
                      className="cards__status-icon"
                      title="Open Access"
                      aria-label="Open Access"
                    >
                      <i className="ai ai-open-access" aria-hidden="true" />
                    </span>
                  )}
                  {item.is_best_paper && (
                    <span
                      className="cards__status-icon"
                      title="Best Paper Award"
                      aria-label="Best Paper Award"
                    >
                      <Award size={16} aria-hidden="true" />
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

FlexPublicationCard.displayName = 'FlexPublicationCard';

FlexPublicationCard.propTypes = {
  item: PropTypes.shape({
    '@id': PropTypes.string.isRequired,
    '@type': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    image_caption: PropTypes.string,
    image_scales: PropTypes.object,
    related_authors_info: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        publication_name: PropTypes.string,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    related_authors: PropTypes.array,
    publication_published_in: PropTypes.string,
    publication_year: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    is_open_access: PropTypes.bool,
    is_best_paper: PropTypes.bool,
    publication_pdf_file: PropTypes.string,
    publication_pre_print_link: PropTypes.string,
    publication_publication_link: PropTypes.string,
    publication_paper_website: PropTypes.string,
    publication_code_repository: PropTypes.string,
    publication_dataset_link: PropTypes.string,
    publication_file_link: PropTypes.string,
    publication_video_link: PropTypes.string,
    publication_video_file: PropTypes.string,
  }).isRequired,
  showAuthorLinks: PropTypes.bool,
  compact: PropTypes.bool,
  hideLink: PropTypes.bool,
};

export default FlexPublicationCard;
